import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import {
  OutputFormulaValue,
  OutputFormulaValueKind,
} from 'src/app/models/output-formulas/output-formula-value';
import {
  OutputFormulaWriteReason,
  OutputFormulaWriteReasonKind,
} from 'src/app/models/output-formulas/output-formula-write-reason';
import { filterEvConventions } from './filename-convention-filters';
import { filterStillsSpecs, filterVideosSpecs } from './file-specification-filters';

export interface OutputFormulaEvUpdateDialogData {
  organizationId: string;
  current: OutputFormula;
  fileSpecifications: FileSpecification[];
  filenameConventions: FilenameConvention[];
}

type EvKind = Extract<OutputFormulaValueKind, 'evStills' | 'evVideos' | 'evBoth'>;
type UpdateReasonKind = Exclude<OutputFormulaWriteReasonKind, 'initialCreate' | 'deleted'>;

const SELECTABLE_REASONS: readonly UpdateReasonKind[] = [
  'preferenceUpdate',
  'correction',
  'other',
];

const REASON_LABELS: Record<UpdateReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

@Component({
  selector: 'app-output-formula-ev-update-dialog',
  templateUrl: './output-formula-ev-update-dialog.component.html',
  styleUrls: ['./output-formula-ev-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OutputFormulaEvUpdateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: UpdateReasonKind): string => REASON_LABELS[k];

  readonly nameControl: FormControl<string>;
  readonly kind: EvKind;

  readonly stillsSpecIdControl = new FormControl<string | null>(null);
  readonly videosSpecIdControl = new FormControl<string | null>(null);
  readonly evConventionIdsControl = new FormControl<string[]>([], { nonNullable: true });

  readonly reasonControl = new FormControl<UpdateReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputFormulaEvUpdateDialogData,
    private readonly dialogRef: MatDialogRef<
      OutputFormulaEvUpdateDialogComponent,
      OutputFormula | undefined
    >,
    private readonly service: OutputFormulaService,
  ) {
    const current = data.current;
    this.nameControl = new FormControl<string>(current.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    });
    this.kind = this.assertEvKind(current.value.kind);
    this.seedSlotControls(current.value);
  }

  private assertEvKind(kind: OutputFormulaValueKind): EvKind {
    if (kind === 'evStills' || kind === 'evVideos' || kind === 'evBoth') return kind;
    throw new Error(`OutputFormulaEvUpdateDialog opened with non-ev kind: ${kind}`);
  }

  private seedSlotControls(value: OutputFormulaValue): void {
    switch (value.kind) {
      case 'evStills':
        this.stillsSpecIdControl.setValue(value.stillsSpecId);
        this.evConventionIdsControl.setValue([...value.evConventionIds]);
        break;
      case 'evVideos':
        this.videosSpecIdControl.setValue(value.videosSpecId);
        this.evConventionIdsControl.setValue([...value.evConventionIds]);
        break;
      case 'evBoth':
        this.stillsSpecIdControl.setValue(value.stillsSpecId);
        this.videosSpecIdControl.setValue(value.videosSpecId);
        this.evConventionIdsControl.setValue([...value.evConventionIds]);
        break;
      default:
        break;
    }
  }

  get stillsFileSpecifications(): FileSpecification[] {
    return filterStillsSpecs(this.data.fileSpecifications);
  }

  get videosFileSpecifications(): FileSpecification[] {
    return filterVideosSpecs(this.data.fileSpecifications);
  }

  get evConventions(): FilenameConvention[] {
    return filterEvConventions(this.data.filenameConventions);
  }

  get needsStillsSpec(): boolean {
    return this.kind === 'evStills' || this.kind === 'evBoth';
  }

  get needsVideosSpec(): boolean {
    return this.kind === 'evVideos' || this.kind === 'evBoth';
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get canSave(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.evConventionIdsControl.value.length === 0) return false;
    if (this.needsStillsSpec && this.stillsSpecIdControl.value === null) return false;
    if (this.needsVideosSpec && this.videosSpecIdControl.value === null) return false;
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    return true;
  }

  save(): void {
    if (!this.canSave) return;
    const writeReason = this.buildWriteReason();
    const value = this.buildValue();
    if (writeReason === null || value === null) return;
    this.submitting = true;
    this.service
      .update(this.data.organizationId, this.data.current.id, {
        name: this.nameControl.value.trim(),
        value,
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OutputFormula) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildWriteReason(): OutputFormulaWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }

  private buildValue(): OutputFormulaValue | null {
    const evConventionIds = this.evConventionIdsControl.value;
    switch (this.kind) {
      case 'evStills': {
        const stillsSpecId = this.stillsSpecIdControl.value;
        if (stillsSpecId === null) return null;
        return { kind: 'evStills', stillsSpecId, evConventionIds };
      }
      case 'evVideos': {
        const videosSpecId = this.videosSpecIdControl.value;
        if (videosSpecId === null) return null;
        return { kind: 'evVideos', videosSpecId, evConventionIds };
      }
      case 'evBoth': {
        const stillsSpecId = this.stillsSpecIdControl.value;
        const videosSpecId = this.videosSpecIdControl.value;
        if (stillsSpecId === null || videosSpecId === null) return null;
        return {
          kind: 'evBoth',
          stillsSpecId,
          videosSpecId,
          evConventionIds,
        };
      }
    }
  }
}
